'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { config } from '@/config/config';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import { getLogger } from '@/logger/logger';
import { bookActiveCart, type BookingPlatform } from '@/services/cart/book-active-cart.service';
import { cartStore, resetCart } from '@/store/cart/cart.store';
import { trackPurchaseFinished } from '@/utils/cart/cart-analytics.util';

export const CHECKOUT_PATH = 'payments';

export const CHECKOUT_ORDER_PARAM = 'orderId';

export const BOOKING_ERROR_MESSAGE = {
  slotUnavailable:
    'Um dos horários escolhidos não está mais disponível. Escolha outro horário para continuar.',
  cartNotFound: 'Não encontramos seu carrinho. Monte o carrinho de novo para continuar.',
  unexpected: 'Não foi possível abrir o pagamento. Tente novamente.',
};

const logger = getLogger('cart.checkout-navigation');

export function buildCheckoutLink(orderId: string): string {
  const base = config.checkout.url.replace(/\/$/, '');
  const query = new URLSearchParams({ [CHECKOUT_ORDER_PARAM]: orderId });

  return `${base}/${CHECKOUT_PATH}?${query.toString()}`;
}

function bookingErrorMessage(cause: unknown): string {
  const status = (cause as { status?: number })?.status;

  if (status === 409) {
    return BOOKING_ERROR_MESSAGE.slotUnavailable;
  }

  if (status === 404) {
    return BOOKING_ERROR_MESSAGE.cartNotFound;
  }

  return BOOKING_ERROR_MESSAGE.unexpected;
}

export function useCheckoutNavigation() {
  const [isOpening, setIsOpening] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const router = useRouter();

  const openCheckout = useCallback(async (): Promise<void> => {
    const platform: BookingPlatform = isMobile ? 'web mobile' : 'web';

    setIsOpening(true);

    try {
      const { invoiceId } = await bookActiveCart(platform);

      trackPurchaseFinished(cartStore.getState().cart?.items ?? [], invoiceId);
      window.location.assign(buildCheckoutLink(invoiceId));
    } catch (cause) {
      const message = bookingErrorMessage(cause);

      logger.error('Failed to create the order before opening the checkout', {
        cart: { event: 'order_creation_failed' },
        error: cause,
      });
      resetCart();
      toast.error(message);
      router.push('/');
      setIsOpening(false);
    }
  }, [isMobile, router, toast]);

  return { openCheckout, isOpening };
}
